import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { PUBLIC_USER_STATUS } from "common/constants";
import {
  useApprovePublicUserMutation,
  useDenyPublicUserMutation,
} from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { track } from "../../../utils/tracking";
import { FlexColumn } from "../../Common/__styles__/Layout";
import {
  ButtonSection,
  Container,
  ContentSection,
  FormSection,
  HeaderSection,
  InputRow,
  PrimaryButtons,
} from "../../Common/__styles__/Modal";
import { Button } from "../../Common/Buttons/Button";
import Modal from "../../Common/Modal";
import { Body } from "../../Common/Typography";
import Label from "../../Inputs/Label";
import Radio from "../../Inputs/Radio";
import Textarea from "../../Inputs/Textarea";
import { PublicUser } from "./PublicUsersTable";

type ApprovalDecision = "approve" | "deny";

interface ApprovalDecisionFormValues {
  decision: ApprovalDecision | undefined;
  reason: string;
}

const DECISION_OPTIONS: Array<{
  value: ApprovalDecision;
  text: string;
}> = [
  { value: "approve", text: "Approve" },
  { value: "deny", text: "Deny" },
];

export interface ApprovalDecisionModalHookArgs {
  onCancel: () => void;
  onUpdate: () => void;
  publicUser: PublicUser;
}

export const useApprovalDecisionModal = ({
  onCancel,
  onUpdate,
  publicUser,
}: ApprovalDecisionModalHookArgs) => {
  const [showApprovalDecisionModal, hideApprovalDecisionModal] = useModal(
    () => (
      <Modal
        onRequestClose={() => {
          onCancel();
        }}
      >
        <ApprovalDecisionForm
          onCancel={onCancel}
          onUpdate={onUpdate}
          publicUser={publicUser}
        />
      </Modal>
    ),
    [publicUser],
  );

  return [showApprovalDecisionModal, hideApprovalDecisionModal] as const;
};

const getDefaultValues = (
  publicUser: PublicUser,
): ApprovalDecisionFormValues => {
  if (publicUser.status === PUBLIC_USER_STATUS.ACTIVE) {
    return { decision: "approve", reason: "" };
  }
  if (publicUser.status === PUBLIC_USER_STATUS.DENIED) {
    return { decision: "deny", reason: publicUser.denialReason ?? "" };
  }
  return { decision: undefined, reason: "" };
};

const ApprovalDecisionForm = ({
  publicUser,
  onCancel,
  onUpdate,
}: ApprovalDecisionModalHookArgs) => {
  const { addSuccessToast, addErrorToast } = useStatusToasts();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm<ApprovalDecisionFormValues>({
    defaultValues: getDefaultValues(publicUser),
    mode: "onChange",
  });

  const selectedDecision = watch("decision");

  useEffect(() => {
    if (selectedDecision === "approve") {
      setValue("reason", "");
    }
  }, [selectedDecision, setValue]);

  const [approvePublicUser, { loading: approving }] =
    useApprovePublicUserMutation({
      onCompleted: () => {
        track("Public User Approved", {
          publicUserId: publicUser.id,
          previousStatus: publicUser.status,
        });
        addSuccessToast(
          `${publicUser.firstName} ${publicUser.lastName} has been approved.`,
        );
        onUpdate();
        onCancel();
      },
      onError: () => {
        addErrorToast(
          "There was an error approving this user. Please try again.",
        );
      },
    });

  const [denyPublicUser, { loading: denying }] = useDenyPublicUserMutation({
    onCompleted: () => {
      track("Public User Denied", {
        publicUserId: publicUser.id,
        previousStatus: publicUser.status,
      });
      addSuccessToast(
        `${publicUser.firstName} ${publicUser.lastName} has been denied.`,
      );
      onUpdate();
      onCancel();
    },
    onError: () => {
      addErrorToast("There was an error denying this user. Please try again.");
    },
  });

  const loading = approving || denying;

  const formOnSave = async (data: ApprovalDecisionFormValues) => {
    if (loading) return;

    if (data.decision === "approve") {
      await approvePublicUser({
        variables: { publicUserId: publicUser.id },
      });
    } else {
      await denyPublicUser({
        variables: {
          publicUserId: publicUser.id,
          reason: data.reason || null,
        },
      });
    }
  };

  return (
    <Container width="narrow">
      <HeaderSection>
        <h1>Manage Approval</h1>
        <Body
          size="default"
          type="regular"
          color="contentSecondary"
          style={{ marginBottom: 0 }}
        >
          Update the approval status for {publicUser.firstName}{" "}
          {publicUser.lastName}
        </Body>
      </HeaderSection>
      <FormSection>
        <ContentSection>
          <InputRow>
            <FlexColumn gap="2xs">
              <Label text="Decision" size="default" type="emphasis" compact />
              <Controller
                control={control}
                name="decision"
                render={({ field }) => (
                  <Radio
                    name="decision"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    options={DECISION_OPTIONS}
                    required
                  />
                )}
              />
            </FlexColumn>
          </InputRow>

          {selectedDecision === "deny" && (
            <InputRow>
              <Controller
                control={control}
                name="reason"
                render={({ field }) => (
                  <Textarea
                    name="reason"
                    label="Reason for denial"
                    description="Your reason for denial will be included in the email sent to the user."
                    value={field.value}
                    onChange={field.onChange}
                    minRows={3}
                  />
                )}
              />
            </InputRow>
          )}
        </ContentSection>
        <ButtonSection>
          <PrimaryButtons>
            <Button
              title="Cancel"
              onPress={onCancel}
              size="medium"
              variant="secondary"
            />
            <Button
              title="Submit"
              onPress={() => handleSubmit(formOnSave)()}
              size="medium"
              variant="primary"
              isDisabled={loading || !selectedDecision || !isDirty}
              isPending={loading}
            />
          </PrimaryButtons>
        </ButtonSection>
      </FormSection>
    </Container>
  );
};

export default ApprovalDecisionForm;
